export enum COUNTRY_CODE {
  AUSTRALIA = "AUS",
  BRAZIL = "BRA",
  CANADA = "CAN",
  FRANCE = "FRA",
  INDIA = "IND",
  INDONESIA = "ID",
  MEXICO = "MX",
  SINGAPORE = "SGP",
  UK = "UK",
  USA = "USA",
}
export interface ISubscription {
  createdAt: string;
  status: "ACTIVE" | "PENDING";
  id: string;
}

export interface ICompany {
  id: string;
  name: string;
  website: string;
  employee_count: number;
  industry: string;
}

export interface AdminIUser {
  allowPasswordReset: boolean;
  city: string;
  countryCode: string;
  created_at: string;
  dob: string;
  email: string;
  entitlement: number;
  firstName: string;
  houseNumber: string;
  id: string;
  isAdmin: false;
  lastName: string;
  needs_password_reset: boolean;
  phone: string;
  recoveryAttempts: number;
  source: string;
  status: boolean;
  street1: string;
  street2: string;
  userEmail: string;
  username: string;
  zipCode: string;
}

export interface ICandidate {
  cid: number;
  email: string;
  regkey: string;
  username: string;
  attempts: number;
  created_at: string;
}

export interface UserVerificationDetails {
  smsVerified: boolean;
  ofacVerified: boolean;
  addressVerified: boolean;
  status: boolean;
  fraud: boolean;
  fraudDescription?: string;
  attachments: AttachmentData[];
}

export interface IUser {
  username: string;
  sign: string;
  firstname: string;
  lastname: string;
  email: string;
  useremail: string;
  admin: boolean;
  countryCode: string;
  entitlement: number;
  phone: string;
  country_code: string;
}

export interface IProfile {
  countryCode?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  entitlement?: number;
  phone?: string;
  dob?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface IApplicant {
  applied_month: number;
  applied_year: number;
  comments?: string;
  create_date: string;
  id: string;
  job_post_review_id: string;
  pass: boolean;
  username: string;
}

export interface IJobFormik {
  jobTitle: string;
  country: string;
  city: string;
  state: string;
  linkToApply: string;
  aboutCompany: string;
  description: string;
  responsibilities: string;
  minRequirements: string;
  desiredExperience?: string;
  requiredSkills?: string;
  additionalInfo: string;
  benefits: string;
  acceptWorkVisa: string;
  offerRelocation: string;
  allowRemote: string;
  minSalary?: number;
  maxSalary?: number;
}

export interface IJob {
  company_name: string;
  company_website: string;
  job_about_company: string;
  job_accept_work_visa: boolean;
  job_additional_info: string;
  job_benefits: string;
  job_city: string;
  job_state: string;
  job_company_id: number;
  job_max_salary: number;
  job_min_salary: number;
  job_created_date: string;
  job_description: string;
  job_id: string;
  job_job_title: string;
  job_link_to_apply: string;
  job_min_requirements: string;
  job_required_skills?: string[];
  job_desired_experience?: string;
  job_offer_relocation: boolean;
  job_responsibilities: string;
  job_country: string;
  job_allow_remote: boolean;
  job_active: boolean;
  job_created_by?: string;
}

export interface IJobPostReview {
  company_industry: string;
  company_name: string;
  company_website: string;
  job_post_review_active: boolean;
  job_post_review_created_date: string;
  job_post_review_id: string;
  job_post_review_job_title: string;
  job_post_review_job_url: string;
  job_post_review_created_by?: string;
}

export interface IJPR {
  id: string;
  active: boolean;
  company_id: number;
  job_title: string;
}

export interface IReferral {
  company_name: string;
  referrals_bio: string;
  referrals_created_date: string;
  referrals_id: string;
  referrals_job_title: string;
  referrals_job_url: string;
  referrals_status: string;
  referrals_candidate: string;
  referrals_last_updated: string;
  referrals_referrer?: string;
  referrals_referral_proof?: string;
}

export interface IReferralLifecycle {
  id: string;
  created_date: string;
  comments: string;
  attachment_id: string;
  referral_id: string;
  status: string;
}

export interface IReferralCandidateDetails {
  firstname: string;
  lastname: string;
}

export interface IReferralAttachment {
  id: string;
  name: string;
  created_date: string;
  referral_id: string;
  url: string;
}

export interface IJobComplaint {
  id: string;
  job_id: string;
  comments: string;
  created_by: string;
  created_date: string;
}

export interface IStatusMsg {
  type: "success" | "error" | "warning" | "info";
  text: any;
}

export interface IListItem {
  title: string;
  subtitle: string;
  icon?: IListComponentIconProps;
}

export interface IListComponentIconProps {
  alt: string;
  path: string;
}

export type IFeedListItem = Omit<IListItem, "icon"> & {
  rightPrimary?: string;
  rightSecondary?: string;
  id: string;
};

export interface IListComponentProps {
  items: IListItem[];
}

export interface IFeedsListComponentProps {
  items: IFeedListItem[];
}

export interface IFeedJobTitle {
  job_job_title: string;
}

export interface ISupportTicket {
  created_date: string;
  description: string;
  id: string;
  status: string;
  title: string;
  username: string;
}

export interface ISupportTicketLifecycle {
  created_date: string;
  last_updated: string;
  description: string;
  title: string;
  status: ISupportTicketLifecycleStatus;
  id: number;
  lifecycle_id: number;
  comments: string;
}

export type ISupportTicketLifecycleStatus =
  | "CREATED"
  | "RESOLVED"
  | "REOPENED"
  | "CLOSED"
  | "UNKNOWN";

// Next status of a lifecycle. Can only be RESOLVED OR REOPENED
export type ISupportTicketLifecycleStatusNext = "RESOLVED" | "REOPENED";

export interface IJobRating {
  job_rating: string;
}

export interface ICompanyReview {
  company_rating: string;
}

export interface IBrowserExtn {
  id: string;
  website_key: string;
  website_page: string;
  job_title_selector: string;
  company_name_selector: string;
}

export interface IPreferenceSkill {
  name: string;
  yrs: number;
}

export interface IUserCredibility {
  id: number;
  username: string;
  type: string;
  job_id?: string;
  job_review_id?: string;
  company_id?: string;
  created_date: string;
  comments?: string;
}

export interface IUserCredibilityStats {
  jpr: number;
  job: number;
  company: number;
}

export interface AttachmentData {
  id: string;
  type?: string;
  createdDate: string;
  url?: string;
  originalFileName: string;
  blobId: string;
}

export interface IMoneyTransfer {
  id: string;
  type: "TRANSFER";
  recipient: string;
  amount: number;
  created_date: string;
  recipient_email: string;
  status: string;
  external_id: string; //paypal order id
  sender_email: string;
  groupid: string;
  disabled?: boolean;
}

export interface IMoneyTransferStatus {
  id: string;

  created_date: string;
  money_transfer_id: string;
  status: string;
}

export interface IMoneyTransferWebHook {
  id: string;

  data: string;
  data_json: any;
  created_date: string;
  order_id: string;
  acknowledged: boolean;
}

export interface IMoneyTransferPayout {
  amount: number;
  created_date: string;
  currency: string;
  id: string;
  money_transfer_id: string;
  payout_batch_id: string;
  payout_item_id: string;
  recipient_email: string;
  recipient_first_name: string;
  recipient_last_name: string;
  recipient_phone: string;
}

export interface IMoneyTransferPayoutItemDetails {
  payout_item_id: string;
  transaction_status: string; // "FAILED"
  payout_batch_id: string;
  sender_batch_id: string;
  errors?: IError;
}

export interface IError {
  name: string; // https://developer.paypal.com/docs/payouts/standard/reports/view-transaction-activities/
  message: string;
  details: any[];
}

interface IBatchHeader {
  payout_batch_id: string;
  batch_status: string;
  time_created?: string;
  time_completed?: string;
  sender_batch_header: CreatePayoutSenderBatchHeader;
}

interface CreatePayoutSenderBatchHeader {
  sender_batch_id: string;
  email_subject: string;
  email_message: string;
}

export interface IMoneyTransferBatchPayoutDetails {
  batch_header: IBatchHeader;
  items: IPayoutItem[];
}

export interface IMoneyTransferFee {
  id: string;
  amount: number;
  charged_to: string;
}

interface IPayoutItem {
  payout_item_id: string;
  transaction_id: string;
  activity_id: string;
  transaction_status: "UNCLAIMED"; // other values not known
  payout_item_fee: IPayoutAmount;
  payout_batch_id: string; // same as batch id
  payout_item: {
    amount: IPayoutAmount;
    recipient_type: "EMAIL" | "IPHONE";
  };
}

// currency is just currency here
export interface IPayoutAmount {
  currency: "USD";
  value: string;
}

export interface IAmount {
  currency_code: "USD";
  value: string;
}

interface IOrderPurchaseUnit {
  amount: IAmount;
  payments?: IOrderPayments; // only for get order capture details
}

export interface IOrderPayments {
  captures: IOrderCapture[];
  refunds: IOrderRefund[];
}

export interface IOrderRefund {
  id: string;
  amount: IAmount;
  status: string; // COMPLETED
}

export interface IOrderCapture {
  id: string;
  status: string;
  amount: IAmount;
  final_capture: boolean;
}

export interface IMoneyTransferOrderCaptureDetails {
  id: string;
  intent: string; // "CAPTURE"
  status: string; // COMPLETED
  purchase_units: IOrderPurchaseUnit[];
}
